import { useState, useCallback, useRef } from 'react';

const REAUTH_TTL_MS = 10 * 60 * 1000;
const SAFETY_BUFFER_MS = 5_000;

// Module-level cache shared across all consumers (intentional — not context-based)
let cachedToken: { value: string; expiresAt: number } | null = null;

export function getCachedReauthToken(): string | null {
  if (!cachedToken) return null;
  if (cachedToken.expiresAt <= Date.now() + SAFETY_BUFFER_MS) {
    cachedToken = null;
    return null;
  }
  return cachedToken.value;
}

export function setCachedReauthToken(value: string) {
  cachedToken = { value, expiresAt: Date.now() + REAUTH_TTL_MS };
}

export function clearReauthToken() {
  cachedToken = null;
}

// Promise-based modal trigger — used by the API interceptor to demand a token.
let pendingResolver: ((token: string | null) => void) | null = null;
let openModalFn: (() => void) | null = null;

export function registerReauthModal(opener: () => void) {
  openModalFn = opener;
}

export function requestReauth(): Promise<string | null> {
  const cached = getCachedReauthToken();
  if (cached) return Promise.resolve(cached);
  if (!openModalFn) return Promise.resolve(null);
  openModalFn();
  return new Promise((resolve) => {
    pendingResolver = resolve;
  });
}

export function resolveReauthRequest(token: string | null) {
  pendingResolver?.(token);
  pendingResolver = null;
}

// Hook used inside <ReauthModal /> to drive the open-state UI.
export function useReauthModalState() {
  const [open, setOpen] = useState(false);
  const opener = useRef(() => setOpen(true));

  // Register the opener once on first render
  useState(() => {
    registerReauthModal(opener.current);
    return null;
  });

  const close = useCallback((token: string | null) => {
    setOpen(false);
    resolveReauthRequest(token);
  }, []);

  return { open, close };
}
